import React, { useCallback, useEffect, useState } from "react";
import { CalendarDays, CheckCircle2, Mail, Plug, RefreshCw, Unplug } from "lucide-react";
import { apiGet, apiPost } from "../../Api/Client.js";
import { resolveActiveEmployee } from "../../Shared/ScreenUtils.jsx";
import "../../Styles/IntegrationsScreen.css";

const base = "/IntegrationHub/api/gmail";
const calendarBase = "/Banao/calendar";

function formatDate(value) {
  if (!value) return "Not checked yet";
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}

const MAX_MEETINGS_SHOWN_PER_DAY = 4;

/* Which calendar day a timestamp falls on, and what time it reads — both
   answered here rather than on the server, because both depend on the viewer's
   timezone and the server runs on UTC. Doing this server-side listed an 8:45am
   meeting as 3:15am and made "today" start at 5:30am. The browser already
   knows the right answer; ask it. */
function localDayKey(date) {
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
}

function groupByDay(events, dayCount) {
  const midnight = new Date();
  midnight.setHours(0, 0, 0, 0);

  const days = [];
  for (let offset = 0; offset < dayCount; offset += 1) {
    const date = new Date(midnight);
    date.setDate(date.getDate() + offset);
    days.push({
      key: localDayKey(date),
      label:
        offset === 0
          ? "Today"
          : offset === 1
            ? "Tomorrow"
            : date.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" }),
      events: [],
    });
  }

  const byKey = new Map(days.map((day) => [day.key, day]));
  (events || []).forEach((event) => {
    const starts = new Date(event.starts_at);
    if (Number.isNaN(starts.getTime())) return;
    const day = byKey.get(localDayKey(starts));
    if (!day) return;
    day.events.push({
      id: event.id,
      title: event.title,
      time: starts.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }),
    });
  });

  return days;
}

/* What actually came out of the sync, in the plainest possible form.
   A connection card can say "connected" and show a fresh sync time while
   having imported precisely nothing — that exact state went unnoticed here for
   weeks. Listing the person's real meetings is the only honest proof the pipe
   is carrying data: if their own meetings are on screen, it works.
   Deliberately just a day, a time and a title — richer calendar views already
   exist in the CRM and this is not one. */
function UpcomingMeetings({ events, dayCount = 2 }) {
  const days = groupByDay(events, dayCount);
  return (
    <div className="Calendar-Upcoming">
      {days.map((day) => {
        const shown = day.events.slice(0, MAX_MEETINGS_SHOWN_PER_DAY);
        const hidden = day.events.length - shown.length;
        return (
          <div className="Calendar-Upcoming-Day" key={day.key}>
            <p className="Calendar-Upcoming-Label">{day.label}</p>
            {shown.length ? (
              <ul>
                {shown.map((event) => (
                  <li key={event.id}>
                    <time>{event.time}</time>
                    <span>{event.title}</span>
                  </li>
                ))}
                {hidden > 0 && (
                  <li className="Calendar-Upcoming-More">
                    <time /> <span>+{hidden} more</span>
                  </li>
                )}
              </ul>
            ) : (
              /* An empty day is stated, not hidden. A missing day reads as
                 "the calendar is broken", which is the exact ambiguity this
                 whole panel exists to remove. */
              <p className="Calendar-Upcoming-Empty">Nothing scheduled</p>
            )}
          </div>
        );
      })}
    </div>
  );
}

function CalendarIntegrations({ data }) {
  const employee = resolveActiveEmployee(data);
  const [connections, setConnections] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [busy, setBusy] = useState("");
  const [notice, setNotice] = useState("");

  const load = useCallback(async () => {
    try {
      // /connections/mine/ is strictly self-scoped, unlike /connections/ (which
      // intentionally surfaces teammates' calendars to sales/BA/staff for the
      // Sales CRM lead-scheduling panel). This page must only ever show and
      // let you disconnect YOUR OWN calendar, the same way Gmail already works.
      const mine = await apiGet(`${calendarBase}/connections/mine/`);
      setConnections((Array.isArray(mine) ? mine : []).filter((item) => item.provider === "google"));
    } catch (error) {
      setNotice(error?.payload?.calendar || error?.message || "Unable to load calendar connections.");
    }
    // Failing to load the meeting preview must never blank out the connection
    // controls above it — it is supporting evidence, not the page.
    try {
      const result = await apiGet(`${calendarBase}/my-upcoming/?days=2`);
      setUpcoming(Array.isArray(result?.events) ? result.events : []);
    } catch {
      setUpcoming([]);
    }
  }, []);

  useEffect(() => {
    load();
    const state = new URLSearchParams(window.location.search).get("calendar");
    if (state === "google_connected") setNotice("Google Calendar connected.");
    if (state === "google_error") setNotice("Google Calendar connection failed. Please try again.");
  }, [load]);

  const connect = async () => {
    if (!employee?.id) {
      setNotice("Unable to identify your employee profile. Contact an admin.");
      return;
    }
    setBusy("connect");
    try {
      const next = `${window.location.pathname}${window.location.search || ""}`;
      const result = await apiGet(
        `${calendarBase}/google/connect/?employee=${employee.id}&next=${encodeURIComponent(next)}`
      );
      window.location.assign(result.authorization_url);
    } catch (error) {
      setNotice(error?.payload?.google || error?.payload?.permission || error?.message || "Unable to start Google Calendar connection.");
      setBusy("");
    }
  };

  const disconnect = async (connection) => {
    setBusy(`disconnect-${connection.id}`);
    try {
      await apiPost(`${calendarBase}/connections/${connection.id}/disconnect/`, {});
      setNotice(`${connection.calendar_email || "Calendar"} disconnected.`);
      await load();
    } catch (error) {
      setNotice(error?.payload?.permission || error?.message || "Unable to disconnect calendar.");
    } finally {
      setBusy("");
    }
  };

  // Pull events for this one calendar right now, rather than waiting up to five
  // minutes for the scheduled job — and, more importantly, report what actually
  // happened. Reporting the count is the point: "synced, 0 events" and "synced,
  // 24 events" look identical from the outside otherwise, which is how a
  // completely broken sync stayed invisible.
  const syncNow = async (connection) => {
    setBusy(`sync-${connection.id}`);
    setNotice("");
    try {
      const result = await apiPost(`${calendarBase}/sync-sales-calendar/`, {
        connection_id: connection.id,
      });
      const count = result?.imported_count ?? 0;
      setNotice(
        result?.sync_skipped
          ? `Skipped: ${result?.skip_reason || "synced very recently"}.`
          : `${connection.calendar_email || "Calendar"}: ${count} event${count === 1 ? "" : "s"} imported.`
      );
      await load();
    } catch (error) {
      setNotice(
        error?.payload?.detail ||
          error?.payload?.calendar ||
          error?.payload?.connection_id ||
          error?.message ||
          "Unable to sync this calendar."
      );
      await load();
    } finally {
      setBusy("");
    }
  };

  return (
    <section className="Integrations-Section">
      <header className="Integrations-Header">
        <div>
          <h1>Calendar</h1>
          <p>Connect one or more Google Calendars for meeting matching and availability.</p>
        </div>
        <button className="Primary-Button" disabled={Boolean(busy)} onClick={connect}>
          <CalendarDays size={17} /> {busy === "connect" ? "Opening Google…" : "Connect Google Calendar"}
        </button>
      </header>

      {notice && <div className="Integrations-Notice" role="status">{notice}</div>}

      {Boolean(connections.length) && <UpcomingMeetings events={upcoming} />}

      <div className="Integrations-Grid">
        {connections.map((connection) => {
          const connected = connection.sync_status === "connected";
          const needsReconnect = Boolean(connection.calendar_email) && !connected;
          return (
            <article className={"Integration-Card ds-card-hover" + (needsReconnect ? " reconnect" : "")} key={connection.id}>
              <div className="Integration-Card-Top">
                <div className="Integration-Mail-Icon"><CalendarDays size={22} /></div>
                <div>
                  <h2>{connection.calendar_email || "Unlabelled calendar"}</h2>
                  <span className={`Integration-Status ${connection.sync_status}`}>
                    {connected && <CheckCircle2 size={14} />}
                    {needsReconnect ? "Disconnected — connect again" : (connection.sync_status || "").replaceAll("_", " ")}
                  </span>
                </div>
              </div>
              <dl>
                <div><dt>Last synced</dt><dd>{formatDate(connection.last_synced_at)}</dd></div>
                <div><dt>Access</dt><dd>Your calendar, and who joined the meeting</dd></div>
              </dl>
              {connection.sync_error && <p className="Integration-Error">{connection.sync_error}</p>}
              {/* Sync now, and always available — not only when the connection
                  claims to be healthy. A calendar that says "connected" and has
                  never actually synced is precisely the state this exists to get
                  someone out of, and Gmail has had its own manual check all
                  along while Calendar had none. */}
              <div className="Integration-Actions">
                <button
                  className="Soft-Button"
                  disabled={Boolean(busy)}
                  onClick={() => syncNow(connection)}
                >
                  <RefreshCw size={15} className={busy === `sync-${connection.id}` ? "Spin" : undefined} />
                  {busy === `sync-${connection.id}` ? "Syncing…" : "Sync now"}
                </button>
                <button className="Soft-Button" disabled={Boolean(busy)} onClick={connect}>
                  Reconnect
                </button>
                {connected && (
                  <button className="Soft-Button danger" disabled={Boolean(busy)} onClick={() => disconnect(connection)}>
                    <Unplug size={15} /> Disconnect
                  </button>
                )}
              </div>
            </article>
          );
        })}
        {!connections.length && (
          <div className="Integrations-Empty">
            <CalendarDays size={28} />
            <h2>No Google Calendars connected</h2>
            <p>You can connect more than one Google account. Each shows here separately.</p>
          </div>
        )}
      </div>
    </section>
  );
}

function GmailIntegrations() {
  const [connections, setConnections] = useState([]);
  const [messages, setMessages] = useState({});
  const [busy, setBusy] = useState("");
  const [notice, setNotice] = useState("");

  const load = useCallback(async () => {
    try {
      setConnections(await apiGet(`${base}/connections/`));
    } catch (error) {
      setNotice(error?.payload?.gmail || error?.message || "Unable to load integrations.");
    }
  }, []);

  useEffect(() => {
    load();
    const state = new URLSearchParams(window.location.search).get("gmail");
    if (state === "connected") setNotice("Gmail account connected.");
    if (state === "cancelled") setNotice("Gmail connection was cancelled.");
  }, [load]);

  const connect = async () => {
    setBusy("connect");
    try {
      const result = await apiGet(`${base}/connect/`);
      window.location.assign(result.authorization_url);
    } catch (error) {
      setNotice(error?.payload?.gmail || error?.message || "Unable to start Gmail connection.");
      setBusy("");
    }
  };

  const verify = async (connection) => {
    setBusy(`verify-${connection.id}`);
    try {
      const result = await apiPost(`${base}/connections/${connection.id}/verify/`, { limit: 8 });
      setMessages((current) => ({ ...current, [connection.id]: result.messages || [] }));
      setNotice(`${connection.account_email} verified.`);
      await load();
    } catch (error) {
      setNotice(error?.payload?.gmail || error?.message || "Gmail verification failed.");
      await load();
    } finally {
      setBusy("");
    }
  };

  const disconnect = async (connection) => {
    setBusy(`disconnect-${connection.id}`);
    try {
      await apiPost(`${base}/connections/${connection.id}/disconnect/`, {});
      setMessages((current) => ({ ...current, [connection.id]: [] }));
      setNotice(`${connection.account_email} disconnected.`);
      await load();
    } catch (error) {
      setNotice(error?.payload?.gmail || error?.message || "Unable to disconnect Gmail.");
    } finally {
      setBusy("");
    }
  };

  return (
    <section className="Integrations-Section">
      <header className="Integrations-Header">
        <div>
          <h1>Gmail</h1>
          <p>Connect Gmail for read-only message visibility. Intranet cannot send, edit, label or delete mail.</p>
        </div>
        <button className="Primary-Button" disabled={Boolean(busy)} onClick={connect}>
          <Mail size={17} /> {busy === "connect" ? "Opening Google…" : "Connect Gmail"}
        </button>
      </header>

      {notice && <div className="Integrations-Notice" role="status">{notice}</div>}

      <div className="Integrations-Grid">
        {connections.map((connection) => {
          const recent = messages[connection.id] || [];
          const connected = connection.connection_status === "connected";
          return (
            <article className="Integration-Card ds-card-hover" key={connection.id}>
              <div className="Integration-Card-Top">
                <div className="Integration-Mail-Icon"><Mail size={22} /></div>
                <div>
                  <h2>{connection.account_email}</h2>
                  <span className={`Integration-Status ${connection.connection_status}`}>
                    {connected && <CheckCircle2 size={14} />}
                    {connection.connection_status.replace("_", " ")}
                  </span>
                </div>
              </div>
              <dl>
                <div><dt>Last successful check</dt><dd>{formatDate(connection.last_successful_check_at)}</dd></div>
                <div><dt>Access</dt><dd>Gmail metadata only</dd></div>
              </dl>
              {connection.sync_error && <p className="Integration-Error">{connection.sync_error}</p>}
              <div className="Integration-Actions">
                {connected ? (
                  <>
                    <button className="Soft-Button" disabled={Boolean(busy)} onClick={() => verify(connection)}>
                      <RefreshCw size={15} /> {busy === `verify-${connection.id}` ? "Checking…" : "Verify & view recent"}
                    </button>
                    <button className="Soft-Button danger" disabled={Boolean(busy)} onClick={() => disconnect(connection)}>
                      <Unplug size={15} /> Disconnect
                    </button>
                  </>
                ) : (
                  <button className="Soft-Button" disabled={Boolean(busy)} onClick={connect}>Reconnect</button>
                )}
              </div>
              {!!recent.length && (
                <div className="Integration-Messages">
                  <h3>Recent message metadata</h3>
                  {recent.map((message) => (
                    <div key={message.id}>
                      <strong>{message.Subject || "(No subject)"}</strong>
                      <span>{message.From || "Unknown sender"}</span>
                      <time>{message.Date || ""}</time>
                    </div>
                  ))}
                </div>
              )}
            </article>
          );
        })}
        {!connections.length && (
          <div className="Integrations-Empty">
            <Mail size={28} />
            <h2>No Gmail accounts connected</h2>
            <p>You can connect more than one account. Calendar connections remain separate.</p>
          </div>
        )}
      </div>
    </section>
  );
}

export function IntegrationsScreen(props) {
  const { data = {} } = props;
  return (
    <div className="Integrations-Screen">
      {/* The warning sits here, once, rather than beside the Calendar button.
          That "connect your own Gmail and Calendar" line said nothing the two
          headings below it don't already say; the unverified-app screen is the
          one thing that genuinely surprises people mid-flow, and it surprises
          them identically for Gmail and for Calendar. */}
      <header className="Integrations-Page-Header">
        <h1><Plug size={22} strokeWidth={1.8} /> Integrations</h1>
        <p className="Integrations-Prewarning">
          Heads up: Google shows a &quot;Google hasn&apos;t verified this app&quot; screen the first time. That is expected — click <strong>Advanced</strong>, then <strong>Go to intranet.atg.party (unsafe)</strong> to continue.
        </p>
      </header>
      <GmailIntegrations />
      <CalendarIntegrations data={data} />
    </div>
  );
}
